import { FC, useMemo } from 'react'
import { Form, Formik } from 'formik'
import { object, number, InferType } from 'yup'
import { useParams } from 'react-router-dom'
import { ModalButtons } from 'ui/Modal'
import { Button } from 'ui/components/Button'
import { useUpdateOrderItemPrice } from 'api/OrderItems'
import { Notification, useNotificationContext } from 'ui/components/Notification'
import { useTranslation } from 'react-i18next'
import { FormikSubmitHandler } from 'utilities/TypeUtils'
import { PriceField } from './PriceField'
import { OfferingSummaryTypeEnum, OrderPurchaseItem } from '@trybeapp/sdk'

type FormData = InferType<ReturnType<typeof useValidationSchema>>

interface EditPriceFormProps {
  onCancel: () => void
  item: OrderPurchaseItem
}

export const EditPriceForm: FC<EditPriceFormProps> = ({ onCancel, item }) => {
  const { orderId } = useParams<{ orderId?: string }>()
  const { mutate, isLoading } = useUpdateOrderItemPrice()
  const notification = useNotificationContext()
  const { t } = useTranslation()
  const validationSchema = useValidationSchema(item)

  const itemPrice = item.unit_price ?? (item as { price?: number }).price
  const initialValues: FormData = {
    price: itemPrice ?? 0,
  }

  const handleSubmit: FormikSubmitHandler<typeof validationSchema> = (values) => {
    if (!orderId) {
      notification.notify(
        <Notification
          title={t('orders.order_details.items.edit_price_overlay.order_id_missing.title')}
          description={t(
            'orders.order_details.items.edit_price_overlay.order_id_missing.description'
          )}
          variant="warning"
          autoDismiss
        />
      )
      return
    }

    mutate([orderId, item.id, { price: values.price }], {
      onSuccess: () => {
        notification.notify(
          <Notification
            title={t('orders.order_details.items.edit_price_overlay.price_updated.title')}
            description={t(
              'orders.order_details.items.edit_price_overlay.price_updated.description'
            )}
            variant="success"
            autoDismiss
          />
        )
        onCancel()
      },
      onError: (error) => {
        notification.notifyApiError(error)
      },
    })
  }

  return (
    <Formik<FormData>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      <Form>
        <PriceField />

        {item.item_type === OfferingSummaryTypeEnum.Voucher && (
          <p className="text-xs text-gray-600 mt-2 italic">
            {t('orders.order_details.items.edit_price_overlay.voucher_info')}
          </p>
        )}

        <ModalButtons>
          <Button variant="primary" type="submit" loading={isLoading}>
            {t('modal_button.update')}
          </Button>
          <Button variant="ghost" className="ml-auto" onClick={onCancel}>
            {t('modal_button.cancel')}
          </Button>
        </ModalButtons>
      </Form>
    </Formik>
  )
}

function useValidationSchema(item: OrderPurchaseItem) {
  const { t } = useTranslation()
  const itemBasePrice =
    item.base_price ?? (item as { price?: number }).price ?? item.unit_price ?? 0

  return useMemo(
    () =>
      object({
        price: number()
          .min(
            itemBasePrice,
            t('orders.order_details.items.edit_price_overlay.cannot_be_lower_price')
          )
          .required(t('orders.order_details.items.edit_price_overlay.validation.price_required')),
      }),
    [t, itemBasePrice]
  )
}
